import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import { Dispatch } from 'redux';
import { AppDispatch, useSelector } from './store/AppStore';
import { clearEvents } from './store/actions/sagaActions/events/CreateEventSagaActions';
import { clearAllEvent } from './store/actions/sagaActions/events/GetAllEventSagaActions';
import { clearSchemas } from './store/actions/sagaActions/events/SchemaNameSagaActions';
import { clearAllSourceRegistry } from './store/actions/sagaActions/schedule-maintenance/GetAllSourceRegistriesSagaActions';
import { clearServiceData } from './store/actions/sagaActions/services/CreateServiceSagaActions';
import { clearEgressServiceEvent } from './store/actions/sagaActions/services/EgressServiceEventSagaAction';
import { clearAllServiceList } from './store/actions/sagaActions/services/GetAllServiceAction';
import { clearIngressServiceEvent } from './store/actions/sagaActions/services/IngressServiceEventSagaActions';
import { clearServiceEventMappings } from './store/actions/sagaActions/services/ServiceEventMappingSagaActions';
import { clearServiceEventSchema } from './store/actions/sagaActions/services/ServiceEventSchemaSagaActions';
import { clearServiceExceptionList } from './store/actions/sagaActions/services/ServiceExceptionListSagaAction';
import { clearServiceOperationList } from './store/actions/sagaActions/services/ServiceOperationsSagaActions';
import { clearedSourceRegistrys } from './store/actions/sagaActions/sources/CreateSourceRegistrySagaActions';
import { clearEventDetails } from './store/reducer/events/EventNameReducer';
import {
  clearEgressEventMapping,
  deleteAllEgressEventMapping
} from './store/reducer/services/EgressEventMappingReducer';
import {
  clearAllExceptions,
  closeEditException
} from './store/reducer/services/ExceptionsReducer';
import {
  clearIngressEventMapping,
  deleteIngressEventMapping
} from './store/reducer/services/IngressEventMappingReducer';
import { resetNavBarState } from './store/reducer/services/NavBarReducer';
import { clearAllSaveAsDraft } from './store/reducer/services/SaveAsDraftReducer';
import { deleteServiceDetails } from './store/reducer/services/ServiceDetailsReducer';
import { clearPageNo } from './store/reducer/services/ServiceExceptionReducer';
import { clearServiceDetails } from './store/reducer/services/ServiceNameReducer';
import {
  clearServiceOperationMonitor,
  setIsEditService,
  setIsEditServiceOperation
} from './store/reducer/services/ServiceOperationMonitorReducer';

interface IMainContentWrapper {
  className: string;
}

const closeExceptionInEdit = (dispatch: Dispatch, pathName: string) => {
  if (
    !pathName.includes('/control-plane/services/viewExceptions')
    && pathName !== '/control-plane/services/exceptions'
  ) {
    dispatch(closeEditException());
  }
};

const pageNoClear = (dispatch: Dispatch, pathName: string) => {
  if (
    !pathName.includes('/control-plane/services/viewServiceDetails')
    && !pathName.includes('/control-plane/services/viewExceptions')
    && !pathName.includes('/control-plane/services/viewServiceOperation')
  ) {
    dispatch(clearPageNo());
    dispatch(clearAllSaveAsDraft());
  }
};

const clearRegistryData = (dispatch: Dispatch, pathName: string) => {
  if (pathName !== '/control-plane/registries/source-registry/create') {
    dispatch(clearedSourceRegistrys());
  }
};

const MainContentWrapper = (prop: IMainContentWrapper) => {
  const { className } = prop;
  const dispatch = useDispatch<AppDispatch>();
  const { serviceOperations } = useSelector(
    (state) => state.serviceOperationReducer
  );
  const { ingressEventMappingList } = useSelector(
    (state) => state.ingressEventMappingReducer
  );
  const { egressEventMappingList } = useSelector(
    (state) => state.egressEventMappingReducer
  );
  const route = useLocation();

  useEffect(() => {
    const pathName = route.pathname;
    if (
      pathName !== '/control-plane/services/createService'
      && pathName !== '/control-plane/services'
      && pathName !== '/control-plane/services/viewServiceDetails'
      && pathName !== '/control-plane/services/viewExceptions'
      && pathName !== '/control-plane/services/viewServiceOperation'
    ) {
      dispatch(clearAllServiceList());
    }

    closeExceptionInEdit(dispatch, pathName);
    pageNoClear(dispatch, pathName);

    if (
      pathName !== '/control-plane/services/createService'
      && pathName !== '/control-plane/services/exceptions'
      && pathName !== '/control-plane/services/serviceOperations'
      && !pathName.includes('/control-plane/services/viewServiceDetails')
      && !pathName.includes('/control-plane/services/viewExceptions')
      && !pathName.includes('/control-plane/services/viewServiceOperation')
    ) {
      dispatch(clearServiceDetails());
      dispatch(deleteServiceDetails());
      dispatch(clearServiceOperationList());
      dispatch(clearIngressEventMapping());
      dispatch(clearEgressEventMapping());
      dispatch(clearAllExceptions());
      dispatch(clearServiceExceptionList());
      dispatch(clearServiceData());
      dispatch(clearServiceEventSchema());
      dispatch(clearServiceEventMappings());
      dispatch(resetNavBarState());
      dispatch(clearServiceOperationMonitor());
      dispatch(setIsEditService(false));
    }

    /**
     * Create-service URL is excluded from the cleanup block above so Redux is not cleared on every
     * render while staying on that route. That means navigating from view/edit service to create
     * can leave the previous service in the store — reset here for a true "new service" flow.
     * Skip when cloning (location.state.cloneServiceName) so clone data can load.
     */
    if (pathName === '/control-plane/services/createService') {
      const cloneServiceName = (route.state as { cloneServiceName?: string } | null)?.cloneServiceName;
      if (!cloneServiceName) {
        dispatch(clearServiceDetails());
        dispatch(deleteServiceDetails());
        dispatch(clearServiceOperationList());
        dispatch(clearIngressEventMapping());
        dispatch(clearEgressEventMapping());
        dispatch(clearAllExceptions());
        dispatch(clearServiceExceptionList());
        dispatch(clearServiceData());
        dispatch(clearServiceEventSchema());
        dispatch(clearServiceEventMappings());
        dispatch(resetNavBarState());
        dispatch(clearServiceOperationMonitor());
        dispatch(setIsEditService(false));
      }
    }

    if (
      pathName !== '/control-plane/events/createEvent'
      && !pathName.includes('control-plane/events/view')
      && !pathName.includes('control-plane/events/edit')
    ) {
      dispatch(clearEvents());
      dispatch(clearSchemas());
      dispatch(clearEventDetails());
    }

    if (
      pathName !== '/control-plane/events/createEvent'
      && pathName !== '/control-plane/events'
    ) {
      dispatch(clearIngressServiceEvent());
      dispatch(clearEgressServiceEvent());
      dispatch(clearAllEvent());
    }

    clearRegistryData(dispatch, pathName);

    if (
      pathName !== '/control-plane/registries/source-registry'
      && !pathName.includes(
        '/control-plane/registries/source-registry/view-source'
      )
    ) {
      dispatch(clearAllSourceRegistry());
    }

    if (pathName !== 'control-plane/services/serviceOperations') {
      dispatch(setIsEditServiceOperation({ isEditServiceOperation: false, index: undefined }));
      const serviceOperationArray = serviceOperations.map(
        (item) => item.serviceOperation
      );
      const serviceOperationArrayFromIngressEvent = ingressEventMappingList.map(
        (item) => item.serviceOperationName
      );
      const eventToBeDeleted = serviceOperationArrayFromIngressEvent.filter(
        (so1) => !serviceOperationArray.some((so2) => so1 === so2)
      );

      // delete the ingress/egress event if for the event, service operation does not exist
      // this covers the following scenario
      // if user clicks on add service operation and adds the ingress event and cancels the adding service operation then related ingress
      // event must be deleted
      if (eventToBeDeleted.length) {
        dispatch(
          deleteIngressEventMapping({
            serviceOperationName: eventToBeDeleted[0]
          })
        );
      }
      // delete associated egress event
      const serviceOperationArrayFromEgressEvent = egressEventMappingList.map(
        (item) => item.serviceOperationName
      );
      const egressEventToBeDeleted = serviceOperationArrayFromEgressEvent.filter(
        (so1) => !serviceOperationArray.some((so2) => so1 === so2)
      );
      egressEventToBeDeleted.forEach((item) => {
        dispatch(deleteAllEgressEventMapping({ serviceOperationName: item }));
      });
    }
  }, [route.pathname, route.state]);
  return (
    <div className={className} data-testid="CIRouteguard">
      <Outlet />
    </div>
  );
};
export default MainContentWrapper;
